import {
  Box,
  Button,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Autocomplete,
  TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import type { User } from '@altyor-kronos/shared-types';

export type CalendarViewMode = 'week' | 'day';

export interface CalendarToolbarProps {
  viewMode: CalendarViewMode;
  onViewModeChange: (mode: CalendarViewMode) => void;
  dateLabel: string;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  /** Only shown for managers/admins. */
  showTeammateSelector: boolean;
  /** Whether to render the "Today" quick-jump button. Defaults to true. */
  showTodayButton?: boolean;
  teammates: Array<{ id: string; data: User }>;
  selectedTeammateId: string;
  onTeammateChange: (userId: string) => void;
}

export default function CalendarToolbar({
  viewMode,
  onViewModeChange,
  dateLabel,
  onPrev,
  onNext,
  onToday,
  showTeammateSelector,
  showTodayButton = true,
  teammates,
  selectedTeammateId,
  onTeammateChange,
}: CalendarToolbarProps) {
  const { t } = useTranslation();

  const teammateOptions = teammates.map((u) => ({
    id: u.id,
    label: u.data.displayName || u.data.email,
  }));

  const selectedOption = teammateOptions.find((o) => o.id === selectedTeammateId) ?? null;

  return (
    <Box
      data-testid="calendar-toolbar"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1, md: 2 },
        px: 2,
        py: 1.5,
        flexWrap: 'wrap',
      }}
    >
      {/* View toggle */}
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(_, v) => v && onViewModeChange(v)}
        size="small"
      >
        <ToggleButton value="week">{t('kronos.calendar.week')}</ToggleButton>
        <ToggleButton value="day">{t('kronos.calendar.day')}</ToggleButton>
      </ToggleButtonGroup>

      {/* Date navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <IconButton onClick={onPrev} size="small" aria-label={t('kronos.calendar.previous')}>
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="h6" sx={{ minWidth: { xs: 120, md: 180 }, textAlign: 'center' }}>
          {dateLabel}
        </Typography>
        <IconButton onClick={onNext} size="small" aria-label={t('kronos.calendar.next')}>
          <ArrowForwardIcon fontSize="small" />
        </IconButton>
      </Box>

      {showTodayButton && (
        <Button variant="outlined" size="small" onClick={onToday}>
          {t('kronos.calendar.today')}
        </Button>
      )}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Teammate selector — managers/admins only */}
      {showTeammateSelector && (
        <Autocomplete
          size="small"
          options={teammateOptions}
          value={selectedOption}
          onChange={(_, v) => onTeammateChange(v?.id ?? '')}
          getOptionLabel={(o) => o.label}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('kronos.calendar.teammate')}
              sx={{ minWidth: { xs: 150, md: 200 } }}
            />
          )}
          disableClearable={false}
        />
      )}
    </Box>
  );
}
